import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Link2, Upload, RotateCcw, Loader2, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shown on My Banks for accounts that came back from "Removed bank accounts"
// and were previously connected: walks the user into reconnecting so the
// period since the last bank line is fetched, or lets them bridge it with a
// statement / keep the account manual.
export const RestoredReconnectBanner = ({ account, canReconnect, reconnecting, onReconnect, onUpload, onChange }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bank-accounts/${account.id}/reconnect-nudge`);
      setInfo(data?.show ? data : null);
    } catch {
      setInfo(null);
    }
  }, [account.id]);

  useEffect(() => { load(); }, [load, account.open_banking_connected, account.needs_reconnect]);

  const dismiss = async (keepManual) => {
    setBusy(true);
    try {
      await axios.post(`${API}/bank-accounts/${account.id}/reconnect-nudge/dismiss`, { keep_manual: keepManual });
      setInfo(null);
      toast.success(keepManual ? `${account.bank_name} will stay a manual account.` : 'Reminder hidden.');
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;
  const gap = info.gap;
  const providerLabel = info.provider === 'plaid' ? 'Plaid' : info.provider === 'mono' ? 'Mono' : 'your bank';

  return (
    <div
      className="mb-4 rounded-xl border border-[#108981]/30 bg-[#108981]/5 px-4 py-3.5 flex flex-col md:flex-row md:items-center gap-3"
      data-testid={`restored-reconnect-banner-${account.id}`}
    >
      <div className="w-10 h-10 rounded-lg bg-[#108981]/15 flex items-center justify-center flex-shrink-0">
        <RotateCcw className="w-5 h-5 text-[#108981]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900" data-testid={`restored-reconnect-title-${account.id}`}>
          {account.bank_name}{account.mask ? ` ····${account.mask}` : ''} is back — reconnect to catch up
        </p>
        <p className="text-xs text-slate-600 mt-0.5" data-testid={`restored-reconnect-gap-${account.id}`}>
          Restored {info.restored_at ? new Date(info.restored_at).toLocaleDateString() : 'recently'} as a manual account.
          {gap?.gap_days > 0
            ? ` Your books stop at ${gap.last_transaction_date}; reconnecting via ${providerLabel} pulls the ${gap.gap_days} day${gap.gap_days === 1 ? '' : 's'} from ${gap.gap_start} to today. Anything the bank can't reach back to can be filled with a statement.`
            : ` Reconnect via ${providerLabel} so new transactions keep flowing in automatically.`}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
        {canReconnect && (
          <Button size="sm" className="bg-[#108981] hover:bg-[#108981]/90" onClick={() => onReconnect(account)}
            disabled={reconnecting} data-testid={`restored-reconnect-btn-${account.id}`}>
            {reconnecting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Link2 className="w-4 h-4 mr-1.5" />}
            Reconnect bank sync
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => onUpload(account)} data-testid={`restored-upload-btn-${account.id}`}>
          <Upload className="w-4 h-4 mr-1.5" /> Upload statement
        </Button>
        <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => dismiss(true)} disabled={busy}
          data-testid={`restored-keep-manual-btn-${account.id}`}>
          Keep manual
        </Button>
        <button onClick={() => dismiss(false)} disabled={busy} className="text-slate-400 hover:text-slate-600 p-1"
          aria-label="Hide" data-testid={`restored-reconnect-dismiss-${account.id}`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default RestoredReconnectBanner;
